import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChartBar, MagnifyingGlass } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { RepCard } from "../components/shared/RepCard";
import { RepCsvUploadModal } from "../components/shared/RepCsvUploadModal";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";
import { usePolling } from "../hooks/usePolling";

const navItems = [{ to: "/admin/rep-activity", label: "Rep Activity", icon: <ChartBar size={18} weight="duotone" />, testId: "nav-admin-rep-activity" }];

const CALLBACK_STATUS_STYLE = {
  pending: "bg-igc-gold/10 text-igc-goldText border-igc-gold/20",
  completed: "bg-igc-success/10 text-igc-success border-igc-success/30",
  missed: "bg-igc-danger/10 text-igc-danger border-igc-danger/40",
};

export default function AdminRepActivityPage() {
  const confirmEdit = useEditConfirm();
  const navigate = useNavigate();
  const [reps, setReps] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [uploadRep, setUploadRep] = useState(null);
  const [resetLeadRep, setResetLeadRep] = useState(null);
  const [resetCategory, setResetCategory] = useState("trust");
  const [resetTarget, setResetTarget] = useState("36");
  const [resetting, setResetting] = useState(false);
  const [wholesaleRep, setWholesaleRep] = useState(null);
  const [wholesaleCategory, setWholesaleCategory] = useState("trust");
  const [wholesaleRunning, setWholesaleRunning] = useState(false);
  const [search, setSearch] = useState("");
  const visibleReps = reps.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (r.display_name || "").toLowerCase().includes(q) || (r.user_id || "").toLowerCase().includes(q);
  });

  const fetchReps = () => {
    api.get("/admin/reps/lead-activity").then(({ data }) => setReps(data)).catch(() => {});
  };

  useEffect(() => {
    fetchReps();
    api.get("/admin/callbacks").then(({ data }) => setCallbacks(data)).catch(() => {});
  }, []);

  usePolling(fetchReps, 15000);

  const openResetLeadCount = (rep) => {
    setResetLeadRep(rep);
    setResetCategory("trust");
    setResetTarget(String(rep.trust_total <= 40 ? rep.trust_total : 36));
  };

  const submitResetLeadCount = async () => {
    const target = parseInt(resetTarget, 10);
    if (Number.isNaN(target) || target < 0) { toast.error("Enter a valid target count"); return; }
    if (!(await confirmEdit(`${resetLeadRep.display_name}'s ${resetCategory} lead count`))) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/reps/${resetLeadRep.id}/reset-lead-count`, { category: resetCategory, target_count: target });
      toast.success(data.message);
      setResetLeadRep(null);
      fetchReps();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const submitWholesaleReplace = async () => {
    setWholesaleRunning(true);
    try {
      await api.post(`/admin/reps/${wholesaleRep.id}/wholesale-replace-leads`, { category: wholesaleCategory });
      toast.success(`Replacing every ${wholesaleCategory} lead for ${wholesaleRep.user_id} — pulling a fresh pool, verifying each one before anything is deleted. This runs in the background and can take 10-20 minutes.`);
      setWholesaleRep(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setWholesaleRunning(false);
    }
  };

  const toggleBlockLeads = async (rep, blocked) => {
    if (!(await confirmEdit(`${rep.display_name}'s lead upload block (${blocked ? "block" : "unblock"})`))) return;
    try {
      const { data } = await api.patch(`/admin/reps/${rep.id}/block-leads`, { blocked });
      toast.success(data.message);
      fetchReps();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleAutoAssignExclude = async (rep, excluded) => {
    try {
      const { data } = await api.patch(`/admin/reps/${rep.id}/exclude-autoassign`, { excluded });
      toast.success(data.message);
      fetchReps();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Rep Activity" moduleBackPath="/admin">
      <div data-testid="admin-rep-activity-page">
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
            <h1 className="font-serif text-3xl text-igc-cream tracking-tight" title="Call activity for every rep's assigned leads, with tools to top up or fully replace a rep's lead list.">Rep Activity</h1>
          </div>
          <p className="text-igc-muted text-sm">Call attempts across every rep's assigned lead list — reps at 35 or fewer trust or funding leads remaining are highlighted. Numbers update automatically in the background every 15s — no refresh needed.</p>
        </div>

        {reps.length === 0 && <p className="text-igc-muted text-sm" data-testid="admin-rep-activity-empty">No reps yet.</p>}

        {reps.length > 0 && (
          <div className="relative mb-4 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-igc-muted" />
            <Input
              data-testid="admin-rep-activity-search-input"
              placeholder="Search by name or rep code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              title="Search narrows the rep cards below in real time as you type — matches name or rep code (e.g. CH001 or just 001)."
              className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream pl-8"
            />
          </div>
        )}
        {reps.length > 0 && visibleReps.length === 0 && (
          <p className="text-igc-muted text-sm" data-testid="admin-rep-activity-search-empty">No reps match "{search}".</p>
        )}

        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="admin-rep-card-grid" data-hscroll>
          {visibleReps.map((r) => (
            <RepCard key={r.id} rep={r} onOpen={(rep) => navigate(`/admin/rep-activity/${rep.id}`)} onUpload={(rep) => setUploadRep(rep)} onResetLeadCount={openResetLeadCount} onWholesaleReplace={(rep) => { setWholesaleRep(rep); setWholesaleCategory("trust"); }} onToggleBlockLeads={toggleBlockLeads} onToggleAutoAssignExclude={toggleAutoAssignExclude} />
          ))}
        </div>

        <div className="mt-12">
          <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-1" title="Every scheduled callback across every rep, so you can see who's due to follow up and when.">Callback Calendar — All Reps</h2>
          <p className="text-igc-muted text-sm mb-4">Every scheduled callback across the system.</p>
          {callbacks.length === 0 && <p className="text-igc-muted text-sm" data-testid="admin-callbacks-empty">No callbacks scheduled.</p>}
          <div className="space-y-2">
            {callbacks.map((c) => (
              <div key={c.id} className={`p-3 border flex items-center justify-between gap-3 flex-wrap ${c.status === "missed" ? "bg-igc-danger/5 border-igc-danger/40" : "bg-igc-surface border-igc-gold/15"}`} data-testid={`admin-callback-row-${c.id}`}>
                <div>
                  <p className="text-igc-cream text-sm">{c.rep_name} ({c.rep_code}) — {c.lead_name}</p>
                  <p className="text-igc-muted text-xs">{formatET(c.scheduled_at, "MMM d, yyyy h:mm a")} ET — Manager: {c.manager_display_name || "Unassigned"}</p>
                </div>
                <span className={`text-[10px] px-2 py-1 uppercase border ${CALLBACK_STATUS_STYLE[c.status]}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>

        {uploadRep && (
          <RepCsvUploadModal
            rep={uploadRep}
            apiBasePath="/admin"
            onClose={() => setUploadRep(null)}
            onSuccess={fetchReps}
          />
        )}

        <Dialog open={!!resetLeadRep} onOpenChange={(v) => { if (!v) setResetLeadRep(null); }}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="reset-lead-count-modal">
            <DialogHeader>
              <DialogTitle className="font-serif">Reset Lead Count</DialogTitle>
              <DialogDescription>Trims an over-inflated lead count down to a target — oldest, never-worked leads are unassigned first, dispositioned leads are never touched.</DialogDescription>
            </DialogHeader>
            {resetLeadRep && (
              <div className="space-y-4">
                <p className="text-igc-muted text-sm">
                  {resetLeadRep.display_name} ({resetLeadRep.user_id}) — currently {resetCategory === "trust" ? resetLeadRep.trust_total : resetLeadRep.funding_total} {resetCategory} leads.
                  Excess, never-worked leads (oldest first) will be unassigned — never deleted, never touching leads with a disposition already logged.
                </p>
                <div>
                  <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">Category</p>
                  <Select value={resetCategory} onValueChange={(v) => { setResetCategory(v); setResetTarget(String(v === "trust" ? (resetLeadRep.trust_total <= 40 ? resetLeadRep.trust_total : 36) : (resetLeadRep.funding_total <= 55 ? resetLeadRep.funding_total : 50))); }}>
                    <SelectTrigger data-testid="reset-lead-count-category-select" title="Choose whether you're trimming this rep's Trust leads or Funding leads." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      <SelectItem value="trust">Trust</SelectItem>
                      <SelectItem value="funding">Funding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">Target Count</p>
                  <Input data-testid="reset-lead-count-target-input" type="number" min="0" value={resetTarget} onChange={(e) => setResetTarget(e.target.value)} title="How many leads this rep should end up with after the excess, never-worked leads are unassigned." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
                </div>
                <Button data-testid="reset-lead-count-confirm-button" onClick={submitResetLeadCount} disabled={resetting} title="Trims this rep's lead count down to the target above." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
                  {resetting ? "Resetting…" : "Reset Lead Count"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!wholesaleRep} onOpenChange={(v) => { if (!v) setWholesaleRep(null); }}>
          <DialogContent className="bg-igc-surface border-igc-danger/40 text-igc-cream" data-testid="wholesale-replace-modal">
            <DialogHeader>
              <DialogTitle className="font-serif text-igc-danger">Replace All Leads</DialogTitle>
              <DialogDescription>
                Deletes EVERY lead in the selected category for this rep — including already-called, already-dispositioned ones — and replaces them with a fresh, fully-verified batch of the same size. Use only when a rep reports bad real-world results despite leads passing verification. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {wholesaleRep && (
              <div className="space-y-4">
                <p className="text-igc-muted text-sm">
                  {wholesaleRep.display_name} ({wholesaleRep.user_id}) — currently {wholesaleCategory === "trust" ? wholesaleRep.trust_total : wholesaleRep.funding_total} {wholesaleCategory} leads, ALL will be replaced.
                </p>
                <div>
                  <p className="text-igc-muted text-xs uppercase tracking-widest mb-1">Category</p>
                  <Select value={wholesaleCategory} onValueChange={setWholesaleCategory}>
                    <SelectTrigger data-testid="wholesale-replace-category-select" title="Choose whether you're replacing this rep's Trust leads or Funding leads entirely." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                      <SelectItem value="trust">Trust</SelectItem>
                      <SelectItem value="funding">Funding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="wholesale-replace-confirm-button" onClick={submitWholesaleReplace} disabled={wholesaleRunning} title="Deletes every lead in this category for this rep and pulls a completely fresh, verified batch. Cannot be undone." className="w-full bg-igc-danger hover:bg-igc-danger/80 text-white">
                  {wholesaleRunning ? "Starting…" : `Replace All ${wholesaleCategory} Leads`}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
